// "Confirm how you found this worker" (GRAVY-10443, AC 14).
//
// graphy decides WHEN the question is asked — a Tenant Admin's manual hire
// onto a flagged (Critical) shift — and refuses the hire with
// SOURCED_FROM_REQUIRED plus the worker's card until it is answered. Every
// admin hire surface (Job page, both staffing modals, the Dashboard V2 invite
// list) posts to the same endpoint, so the retry lives here, once:
// `withHireSourcing` runs the hire, and on that refusal asks the admin through
// the prompt mounted in App, then runs it again with the answer.

export const SOURCED_FROM_OPTIONS = [
  { value: 'phone_call', label: 'Phone call' },
  { value: 'preferred_pool', label: 'Preferred worker pool' },
  { value: 'manual_search', label: 'Manual search' },
  { value: 'other', label: 'Other' },
] as const;

export type SourcedFrom = (typeof SOURCED_FROM_OPTIONS)[number]['value'];

export type HireSource = { sourcedFrom: SourcedFrom; note?: string };

/** The worker card graphy sends with the refusal (Figma 40569:193560). */
export type HireSourceWorker = {
  id: number;
  name?: string | null;
  avatar_url?: string | null;
  average_rating?: number | null;
  ratings_count?: number | null;
  skill_name?: string | null;
  skill_shifts?: number | null;
  shifts_with_client?: number | null;
};

export const SOURCED_FROM_REQUIRED = 'SOURCED_FROM_REQUIRED';

type Prompter = (worker: HireSourceWorker) => Promise<HireSource | null>;

let prompter: Prompter | null = null;

/** Registered by <HireSourcingPrompt/>; returns the unregister function. */
export const registerHireSourcePrompter = (next: Prompter) => {
  prompter = next;
  return () => {
    if (prompter === next) prompter = null;
  };
};

/**
 * The worker card, when `error` is graphy asking how the worker was found;
 * null for any other error. Reads both shapes a hire fails with here — the
 * JobActionError JobsAPI throws and the raw axios error Dashboard V2 sees.
 */
export const sourcingRequest = (error: unknown): HireSourceWorker | null => {
  const e = error as {
    code?: string;
    payload?: { data?: { worker?: HireSourceWorker } };
    response?: {
      data?: { errors?: string[]; data?: { worker?: HireSourceWorker } };
    };
  };
  if (e?.code === SOURCED_FROM_REQUIRED) return e.payload?.data?.worker ?? null;
  const body = e?.response?.data;
  if (body?.errors?.[0] === SOURCED_FROM_REQUIRED)
    return body.data?.worker ?? null;
  return null;
};

/** The request fields for an answer, as graphy names them. */
export const hireSourceParams = (source?: HireSource) =>
  source
    ? {
        sourced_from: source.sourcedFrom,
        sourced_from_note: source.note ?? null,
      }
    : {};

/**
 * Runs `hire`; if graphy asks how the worker was found, asks the admin and runs
 * it again with the answer. A cancelled prompt — or no prompt mounted — rethrows
 * the original refusal, so each caller's existing error handling reports it.
 */
export const withHireSourcing = async <T>(
  hire: (source?: HireSource) => Promise<T>,
): Promise<T> => {
  try {
    return await hire();
  } catch (error) {
    const worker = sourcingRequest(error);
    if (!worker || !prompter) throw error;

    const source = await prompter(worker);
    if (!source) throw error;
    return hire(source);
  }
};
